import type { CreateContractFormSchemaType } from "@/pages/shipper-contract-create/model/validation";
import { VEHICLE_WEIGHT_CLASSES } from "@/shared/constants/offer-cargo";
import { BaseAccordion } from "@/shared/ui/base-accordion";
import { BaseAccordionSummary } from "@/shared/ui/base-accordion-summary";
import { BaseDivider } from "@/shared/ui/base-divider";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseSelect } from "@/shared/ui/base-select";
import { BaseTextField } from "@/shared/ui/base-text-field";
import {
  AccordionDetails,
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { FreightMatching } from "@x-mile/api-contract";
import { type Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const VehicleForm = ({
  deliveryProposalOfferCargo,
  control,
}: {
  deliveryProposalOfferCargo: FreightMatching.schemas.OfferCargo;
  control: Control<CreateContractFormSchemaType>;
}) => {
  const { t } = useTranslation();
  const VEHICLE_SHAPE = Object.keys(
    FreightMatching.schemas.OfferCargoVehicleShape,
  ) as FreightMatching.schemas.OfferCargoVehicleShape[];

  return (
    <BaseAccordion>
      <BaseAccordionSummary>車両</BaseAccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems="flex-start"
            gap={3}
          >
            <BaseFormLabel
              isRequired
              label="トン数(車格)"
              sx={{ maxWidth: "376px" }}
            >
              <Box display="flex" sx={{ alignItems: "flex-end" }}>
                <Controller
                  name="vehicleWeight"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <FormControl size="small" fullWidth>
                        <BaseSelect
                          {...field}
                          type="number"
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                        >
                          {VEHICLE_WEIGHT_CLASSES.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </BaseSelect>
                      </FormControl>
                    );
                  }}
                />
                <Typography fontSize="14px" ml={1}>
                  t
                </Typography>
              </Box>
            </BaseFormLabel>
            <BaseFormLabel
              label="ボディタイプ(車体の形状)"
              sx={{ maxWidth: "376px" }}
              isRequired
            >
              <Controller
                name="vehicleShape"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl size="small" fullWidth>
                    <BaseSelect
                      {...field}
                      type="string"
                      placeholder="未選択"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    >
                      {VEHICLE_SHAPE.map((type) => (
                        <MenuItem key={type} value={type}>
                          {t(`vehicleShape.${type}`)}
                        </MenuItem>
                      ))}
                    </BaseSelect>
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </BaseFormLabel>
          </Box>
          <BaseFormLabel
            label="車両の条件(重量、形状、冷蔵設備の有無、装飾品など)"
            sx={{ mt: 3 }}
          >
            <Controller
              name="vehicleEquipmentMemo"
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  size="small"
                  fullWidth
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  sx={{
                    maxWidth: "376px",
                  }}
                />
              )}
            />
          </BaseFormLabel>
          <BaseDivider />
          {/* 車主提案 */}
          <BaseFormLabel label="装備品" isSuggestedByCarrier sx={{ mt: 3 }}>
            <BaseTextField
              size="small"
              fullWidth
              // 車主提案項目は編集不可
              disabled
              value={
                deliveryProposalOfferCargo?.vehicle?.equipments
                  ? deliveryProposalOfferCargo.vehicle.equipments.join(", ")
                  : "未記入"
              }
              sx={{
                maxWidth: "376px",
              }}
            />
          </BaseFormLabel>
        </Stack>
      </AccordionDetails>
    </BaseAccordion>
  );
};
